/**
 * portalReferrals — the shared vocabulary of the staff referral surface
 * (clinician-portal P1 §6; review 2026-07-26 §C14/§C15/§C16 and §B12).
 *
 * This module exists so THREE screens can agree without importing each other:
 * the panel in CRM Settings, the nav badge (useNavCounts), and the Today
 * worklist card. It holds the row shape, the collision age/SLA rules, the
 * send-outcome copy, the translation of a carrier failure code into the phone
 * call it implies, and — deliberately — every network call the referral surface
 * makes.
 *
 * WHY THE CALLS LIVE HERE. `resendReferralLink` reaches an endpoint that is
 * being built alongside this UI, so its exact path and response shape are the
 * one thing here that could be wrong. Confining it to a single function means
 * rewiring it is a one-line change rather than a hunt through three components.
 * Everything it returns is normalised into `SendOutcome` before it leaves, and
 * `describeSend` is the only place send copy is written — §C16 was a screen
 * that said "has been texted" over a failed send, and a second copy of that
 * sentence is how it comes back.
 *
 * THE LIST IS A PROJECTION. `listReferrals` on the server returns no `enc_*`
 * column and never decrypts: the referred surname, phone, email and note stay
 * sealed until someone opens ONE referral through the triage route, which
 * writes a `phi_accessed` audit row. Nothing in this module opens escrow, and
 * nothing that polls (the nav badge, the panel's 45s tick) may ever start to —
 * a background poll that decrypted would make "who looked at this patient"
 * unanswerable.
 */
import api from '../services/api';

/** The projection the staff list endpoint returns. */
export interface PortalReferral {
  id: string;
  status: string;
  display_first_name: string;
  typed_first_name: string | null;
  typed_last_name: string | null;
  condition_hint: string | null;
  source: 'portal' | 'manual';
  sms_blocked: boolean;
  sms_error: string | null;
  collision_note: string | null;
  contact_id: string | null;
  artifact_error: string | null;
  created_at: string;
  consented_at: string | null;
  consent_expires_at: string | null;
  /**
   * Server-side age/SLA signals for a collision. ALL OPTIONAL ON PURPOSE — they
   * are being added in the same pass as this UI and the API may roll after the
   * frontend does, so every reader here falls back to `created_at`. Do not make
   * any of them required without checking what the endpoint actually emits.
   */
  collision_flagged_at?: string | null;
  collision_at?: string | null;
  collision_age_hours?: number | null;
  collision_sla_state?: string | null;
}

export const errMsg = (e: any): string =>
  e?.response?.data?.detail || e?.response?.data?.error || e?.message || 'Request failed';

// ---------------------------------------------------------------------------
// Collision age and SLA
// ---------------------------------------------------------------------------

/**
 * How long a name mismatch may sit before it is late.
 *
 * There is no contractual SLA — these numbers come from what the patient was
 * told. The consent page ends a collision with "someone from our team will
 * call", so the clock starts the moment they read that, and a same-day call is
 * the promise being kept. `attention` is the nudge, `late` is the breach.
 * Nothing enforces these server-side; they exist so the queue has a shape a
 * recruiter can triage by instead of a flat list sorted by chance.
 */
export const COLLISION_SLA = { attentionHours: 4, lateHours: 24 } as const;

export type CollisionSla = 'fresh' | 'attention' | 'late';

/**
 * When a referral entered collision review, as an ISO string.
 *
 * Prefers whatever the server says, in decreasing order of precision, and falls
 * back to `created_at`. The fallback is not merely defensive: until the server
 * records a flagged-at timestamp, "referred at" is the only honest lower bound
 * on how long the patient has been waiting, and it errs towards showing the
 * queue as OLDER than it is — the safe direction for a queue whose whole
 * problem is that nobody looks at it.
 */
export function collisionSince(r: PortalReferral, now: number = Date.now()): string {
  if (r.collision_flagged_at) return r.collision_flagged_at;
  if (r.collision_at) return r.collision_at;
  if (typeof r.collision_age_hours === 'number' && Number.isFinite(r.collision_age_hours)) {
    return new Date(now - r.collision_age_hours * 3_600_000).toISOString();
  }
  return r.created_at;
}

/** Hours a collision has been waiting, or 0 when the timestamp is unreadable. */
export function collisionAgeHours(r: PortalReferral, now: number = Date.now()): number {
  const then = new Date(collisionSince(r, now)).getTime();
  if (Number.isNaN(then)) return 0;
  return Math.max(0, (now - then) / 3_600_000);
}

/**
 * The SLA verdict for one collision. A server-supplied verdict wins when it is
 * one we understand — if the API starts computing this against business hours
 * or a tenant setting, that answer is better than ours and should not be
 * silently overridden by a client clock.
 */
export function collisionSla(r: PortalReferral, now: number = Date.now()): CollisionSla {
  const fromServer = r.collision_sla_state;
  if (fromServer === 'fresh' || fromServer === 'attention' || fromServer === 'late') return fromServer;
  const hours = collisionAgeHours(r, now);
  if (hours >= COLLISION_SLA.lateHours) return 'late';
  if (hours >= COLLISION_SLA.attentionHours) return 'attention';
  return 'fresh';
}

export interface CollisionSummary {
  /** Referrals sitting in `collision_review`. */
  total: number;
  /** Of those, how many are past `COLLISION_SLA.lateHours`. */
  late: number;
  /** When the oldest one started waiting (ISO), or null when there are none. */
  oldestSince: string | null;
}

export function summariseCollisions(rows: PortalReferral[], now: number = Date.now()): CollisionSummary {
  const open = rows.filter((r) => r.status === 'collision_review');
  let oldest: number | null = null;
  let late = 0;
  for (const r of open) {
    if (collisionSla(r, now) === 'late') late += 1;
    const t = new Date(collisionSince(r, now)).getTime();
    if (!Number.isNaN(t) && (oldest === null || t < oldest)) oldest = t;
  }
  return { total: open.length, late, oldestSince: oldest === null ? null : new Date(oldest).toISOString() };
}

// ---------------------------------------------------------------------------
// Why a text did not go out — §B16
// ---------------------------------------------------------------------------

/**
 * Carrier failure codes, in the words a recruiter can act on.
 *
 * §B16 replaced the carrier's own sentence in `crm.referrals.sms_error` with a
 * CODE, because Twilio's message embeds the destination verbatim ("The 'To'
 * phone number: +1615…") and that number is precisely what escrow encrypts. The
 * column is now the right thing to store — and exactly the wrong thing to show:
 * 21610 (they replied STOP), 21614 (it is a landline) and 21211 (it is not a
 * real number) are three different phone calls, and none of them is legible from
 * the digits. So the translation happens HERE, once, on the way to the screen.
 *
 * Every sentence names the action, because the action is the whole reason the
 * banner exists. Nothing here promises a retry: whether one is scheduled is
 * carried by the attempt tag below, not inferrable from the code.
 */
const SMS_ERROR_COPY: Record<string, string> = {
  send_failed: 'We could not reach the carrier at all, so nothing was sent. Try again, or hand the authorization link over in the office.',
  21211: 'That is not a real phone number, so nothing was sent. Check the digits against the referral and enter it again.',
  21214: 'That number is not a mobile line, so it cannot receive texts. Call the referring practice for a mobile number.',
  21217: 'That number cannot receive texts. Call the referring practice for a mobile number.',
  21408: 'We are not enabled to text that country. Tell the team; hand the authorization link over in the office meanwhile.',
  21606: 'Our own sending number is not set up to text — nothing is wrong with the number the practice gave us. Tell the team.',
  21610: 'The patient replied STOP to our texts, so the carrier will not deliver another one. Texting again will not reach them — call them, or hand the authorization link over in person.',
  21612: 'Our sending number cannot reach that carrier. Texting again will not help — hand the authorization link over in person and tell the team.',
  21614: 'That number is a landline and cannot receive texts. Call the referring practice for a mobile number.',
  30003: 'The handset could not be reached — switched off, out of coverage, or no longer in service.',
  30004: 'The carrier blocked our message to that number.',
  30005: 'That number is no longer in service.',
  30006: 'That number is a landline, or its carrier cannot take our texts.',
  30007: 'The carrier filtered our message as spam. Call the patient rather than texting again.',
};

/** ` [attempt 2/3]`, appended by crmReferralSends on a transient failure only. */
const ATTEMPT_TAG = /\[attempt (\d+)\/(\d+)\]/;

/**
 * The code, from either writer of this column.
 *
 * `twilio_error_21610` is what the send path records; `error 21610: <carrier
 * sentence>` is what the Twilio status webhook records (crmStatus). The webhook
 * form still carries the carrier's unredacted sentence — and therefore the
 * patient's phone number — so this deliberately captures the CODE and drops the
 * tail. See the cross-lane note: the writer should be redacting it too, but this
 * screen must not depend on that landing first.
 */
const SMS_ERROR_CODE = /twilio_error_(\d+)|(?:^|\s)error[ _](\d+)/;

export interface SmsFailure {
  /** Plain English, ending in what to do about it. */
  sentence: string;
  /** "Attempt 2 of 3…" or '' when the stored string carried no counter. */
  attempt: string;
  /** Kept ONLY so an unmapped code can be quoted to support. */
  code: string | null;
}

/**
 * Turn whatever is in `sms_error` into something a staff member can act on, or
 * null when the column is empty.
 *
 * An unknown code gets a generic sentence with the code in brackets rather than
 * a bare `twilio_error_30012`: the sentence is what the recruiter acts on and
 * the code is what support needs, so neither is dropped and neither is left to
 * be decoded by whoever is reading the screen.
 */
export function describeSmsError(raw: string | null | undefined): SmsFailure | null {
  const text = String(raw ?? '').trim();
  if (!text) return null;

  const tag = ATTEMPT_TAG.exec(text);
  let attempt = '';
  if (tag) {
    const [n, of] = [Number(tag[1]), Number(tag[2])];
    attempt = n >= of
      ? `We tried ${of} times and have stopped retrying.`
      : `That was attempt ${n} of ${of}; it retries automatically.`;
  }

  const m = SMS_ERROR_CODE.exec(text);
  const code = m ? (m[1] || m[2]) : null;
  // No code at all: the send path writes the literal `send_failed` (and
  // crmReferralSends' own fallback writes `send failed`) when the throw carried
  // no code, which means we never got as far as the carrier.
  const key = code || (/send[_ ]failed/i.test(text) ? 'send_failed' : null);
  const known = key ? SMS_ERROR_COPY[key] : undefined;

  return {
    sentence: known
      || `The carrier would not take it${code ? ` (code ${code})` : ''}. Try again, or call the patient and hand the authorization link over in the office.`,
    attempt,
    code,
  };
}

// ---------------------------------------------------------------------------
// Send outcomes — §C16
// ---------------------------------------------------------------------------

export interface SendOutcome {
  sent: boolean;
  /** Held for quiet hours; the worker texts it at the next allowed moment. */
  deferred: boolean;
  /** The number is on the network-wide STOP list, so nothing was attempted. */
  blocked: boolean;
  /** Returned ONCE, when the server chooses to hand the link back. */
  consentUrl: string;
  /**
   * Re-armed for the send worker rather than sent inline (§B12 staff resend).
   * Distinct from `deferred`, which means specifically "held for quiet hours" —
   * saying that when it is not true is the same class of lie as §C16.
   */
  queued: boolean;
}

/**
 * Normalise whatever a create/resend response carries into `SendOutcome`.
 *
 * Tolerant by design: the create route answers `{ referral: { sms, smsBlocked,
 * consentUrl } }` and the resend route is newer, so this accepts the payload at
 * the top level or under `referral`, and treats missing flags as "not sent"
 * rather than assuming success. Guessing `sent` is exactly the §C16 defect.
 */
export function readSendOutcome(payload: any): SendOutcome {
  const body = payload?.referral && typeof payload.referral === 'object' ? payload.referral : payload || {};
  const sms = body.sms && typeof body.sms === 'object' ? body.sms : body;
  return {
    sent: Boolean(sms.sent),
    deferred: Boolean(sms.deferred),
    blocked: Boolean(body.smsBlocked ?? body.sms_blocked ?? sms.blocked ?? sms.skipped),
    consentUrl: typeof body.consentUrl === 'string' ? body.consentUrl : '',
    queued: Boolean(body.queued ?? sms.queued),
  };
}

/**
 * The one place send copy is written.
 *
 * §C16: the manual-entry screen claimed the link "has been texted" whichever of
 * these four things happened, including the two where no text exists — and the
 * API has always returned `sms.sent`. Staff who believe a text went out do not
 * hand over the QR, do not call, and find out fourteen days later when the
 * escrow expires. Each branch below has to be able to stand next to the row's
 * real status without contradicting it.
 */
export function describeSend(outcome: SendOutcome, verb: 'created' | 'resent'): string {
  const stem = verb === 'created' ? 'Created' : 'The link is unchanged';
  if (outcome.blocked) {
    return `${stem}. That number has opted out of texts, so nothing was sent — use the link below in the office.`;
  }
  // BEFORE the quiet-hours branch, because a staff resend answers deferred:true
  // as well — it re-arms the ladder and the send worker does the sending. Naming
  // quiet hours as the reason when the real reason is "the worker has it" would
  // be a confident, specific, wrong explanation.
  if (outcome.queued) {
    return verb === 'created'
      ? 'Created. The text is queued and goes out on the next send pass.'
      : 'The link is unchanged and queued to be texted again on the next send pass.';
  }
  if (outcome.deferred) {
    return `${stem}. It is quiet hours where the patient is, so the text is queued and goes out first thing in the morning.`;
  }
  if (outcome.sent) {
    return verb === 'created'
      ? 'Created and the authorization link has been texted.'
      : 'The authorization link has been texted again.';
  }
  return `${stem}, but the text did not go out. Try again, or use the link below in the office.`;
}

// ---------------------------------------------------------------------------
// Calls
// ---------------------------------------------------------------------------

/** The staff referral list. Projection only — see the module header. */
export async function listReferrals(status?: string): Promise<PortalReferral[]> {
  const res = await api.get('/api/crm/portal-admin/referrals', { params: status ? { status } : {} });
  return (res.data?.referrals || []) as PortalReferral[];
}

/**
 * The backlog COUNT, without the names.
 *
 * The list projection above carries `display_first_name`, `typed_first_name`,
 * `typed_last_name` and `condition_hint` — a name plus a condition, i.e. PHI —
 * and the list route writes no audit row because it was built to answer a screen
 * someone deliberately opened. Two things now want only an integer from it: the
 * nav badge and Today's card, both of which poll in the background for as long
 * as the CRM is open. Shipping every collision patient's name into a browser
 * every 60 seconds to render a number is a disclosure bought for nothing, so the
 * badge asks a count-only endpoint instead.
 *
 * `crm.referral_collision_backlog(integer)` already computes exactly this shape
 * and is granted to service_role; the HTTP route in front of it is being added
 * alongside this change. Until it lands anywhere this bundle is deployed, one
 * 404 is remembered for the life of the page and the old list is used, because a
 * badge that vanishes is a worse failure than the one being fixed — the queue's
 * whole problem is that nobody looks at it. The warning is there so this cannot
 * quietly become permanent.
 */
const COLLISION_SUMMARY_PATH = '/api/crm/portal-admin/referrals/collision-summary';
let summaryEndpointMissing = false;

/**
 * Accept either the endpoint's own vocabulary or the SQL function's, so a route
 * that passes `referral_collision_backlog` straight through works unchanged.
 * Returns null for anything unrecognisable — the caller falls back rather than
 * rendering "0 waiting" from a payload it did not understand.
 */
function readCollisionSummary(payload: any): CollisionSummary | null {
  const body = payload?.summary && typeof payload.summary === 'object' ? payload.summary : payload;
  if (!body || typeof body !== 'object') return null;
  const total = body.total ?? body.pending;
  if (typeof total !== 'number' || !Number.isFinite(total)) return null;
  const late = body.late ?? body.stale;
  const oldest = body.oldestSince ?? body.oldest_flagged_at ?? body.oldest_at ?? null;
  return {
    total: Math.max(0, Math.trunc(total)),
    late: typeof late === 'number' && Number.isFinite(late) ? Math.max(0, Math.trunc(late)) : 0,
    oldestSince: typeof oldest === 'string' && oldest ? oldest : null,
  };
}

export async function fetchCollisionSummary(now: number = Date.now()): Promise<CollisionSummary> {
  if (!summaryEndpointMissing) {
    try {
      const res = await api.get(COLLISION_SUMMARY_PATH);
      const summary = readCollisionSummary(res.data);
      if (summary) return summary;
      summaryEndpointMissing = true;
      console.warn('[crm] collision-summary answered a shape this build does not understand; falling back to the referral list.');
    } catch (e: any) {
      const status = e?.response?.status;
      // Only "there is no such route" falls back. A 403/502 is a real failure of
      // an endpoint that exists, and retrying the name-carrying list behind it
      // would defeat the point.
      if (status !== 404 && status !== 501) throw e;
      summaryEndpointMissing = true;
      console.warn(
        '[crm] collision-summary endpoint is not deployed; the badge is falling back to the referral list, '
        + 'which carries patient names. See review §C14.',
      );
    }
  }
  // Fallback only. Deliberately the default page size rather than the 200 the
  // badge used to ask for: fewer names on the wire, and a backlog past 100 is an
  // emergency the exact number does not change.
  return summariseCollisions(await listReferrals('collision_review'), now);
}

/**
 * §B12 — re-text (and re-reveal) a manual referral's authorization link.
 *
 * THE ONE CALL IN THIS MODULE WHOSE SHAPE IS AN ASSUMPTION. A manual referral
 * has no portal user, so the clinician's own resend cannot reach it: before
 * this, a single Twilio timeout left the row as "Couldn't text" with no actor
 * at all, the consent URL having been shown exactly once and the triage loader
 * deliberately never opening the escrowed token. The referral then sat until
 * the 14-day expiry shredded it.
 *
 * Assumed contract: POST /api/crm/portal-admin/referrals/:id/resend, answering
 * with the same `{ sms, smsBlocked, consentUrl? }` shape the create route uses.
 * `readSendOutcome` absorbs reasonable variation; a different PATH is the only
 * thing that needs editing, and only here.
 */
export async function resendReferralLink(referralId: string): Promise<SendOutcome> {
  const res = await api.post(`/api/crm/portal-admin/referrals/${referralId}/resend`, {});
  return readSendOutcome(res.data);
}

/**
 * Statuses where re-texting the link is meaningful: the patient has not signed,
 * the referral has not closed, and the escrowed token is still live. A signed
 * or withdrawn referral has nothing left to send, and `collision_review` is
 * excluded because the resolve step re-arms the send itself — offering both
 * would let staff text an identity they have not yet verified.
 */
export const RESENDABLE_STATUSES = new Set([
  'pending_consent', 'sms_failed', 'delivered', 'reminded', 'viewed',
]);

/**
 * Whether the staff resend control applies to this row. Manual only: a portal
 * referral belongs to a clinician who has their own resend button in the portal
 * and whose grant scopes it, and duplicating that here would text the patient
 * twice from two surfaces that cannot see each other.
 *
 * `sms_blocked` is excluded conservatively rather than correctly. Those rows are
 * the ones that most need the link re-shown — the QR in the office is their only
 * path — but the control's one action is a POST to a send endpoint, and pressing
 * it against a number on the STOP list must never be able to text. Once that
 * endpoint is confirmed to re-check `sms_optout_index` and return `consentUrl`
 * WITHOUT sending, drop the `!r.sms_blocked` term here and nothing else changes.
 */
export function canStaffResend(r: PortalReferral): boolean {
  return r.source === 'manual' && !r.sms_blocked && RESENDABLE_STATUSES.has(r.status);
}
